/**
 * Architecture project timeline helpers (Firestore `timelineData/_info` per capture).
 * Client sorts by real-world `sceneDate` when present, else `createdAt`, then `orderIndex`.
 */

import { getCaptureLatLng } from './captureTimelineGeo';

/**
 * Display label for a capture on the architecture timeline / overlay rail (always ALL CAPS).
 * @param {unknown} raw
 * @returns {string}
 */
export function formatTimelineCaptureLabel(raw) {
  const s = typeof raw === 'string' ? raw.trim() : '';
  return (s || 'CAPTURE').toUpperCase();
}

/**
 * Epoch ms from a Firestore Timestamp, plain `{ seconds, nanoseconds }` (e.g. JSON), or number.
 * Without this, `sceneDate` can appear "unsaved" after reload because the UI only checked `.toMillis`.
 * @param {unknown} ts
 * @returns {number} milliseconds since epoch, or 0 if missing / invalid
 */
export function firestoreTimestampLikeToMs(ts) {
  if (ts == null || ts === '') return 0;
  try {
    if (typeof ts.toMillis === 'function') return ts.toMillis();
    if (typeof ts === 'object' && typeof ts.seconds === 'number') {
      const ns = typeof ts.nanoseconds === 'number' ? ts.nanoseconds : 0;
      return ts.seconds * 1000 + Math.floor(ns / 1e6);
    }
    if (typeof ts === 'number' && Number.isFinite(ts)) return ts;
  } catch {
    return 0;
  }
  return 0;
}

/** @param {unknown} ts */
function snapshotCreatedMs(ts) {
  return firestoreTimestampLikeToMs(ts);
}

/**
 * Sort/display time for a timeline step: ACTUAL DATE (`sceneDate`) first, else capture `createdAt`, else snapshot `createdAt`.
 * @param {Record<string, unknown>} step — snapshot doc
 * @param {Record<string, unknown> | null | undefined} [capture]
 * @returns {number} epoch ms (0 if unknown)
 */
export function snapshotTimelineStepMs(step, capture) {
  return (
    snapshotCreatedMs(step?.sceneDate) ||
    snapshotCreatedMs(capture?.createdAt) ||
    snapshotCreatedMs(step?.createdAt)
  );
}

/**
 * Sort key: scene date (footage in real life), then capture snapshot createdAt.
 * @param {Record<string, unknown>} s
 */
export function snapshotTimelineSortMs(s) {
  return snapshotTimelineStepMs(s, null);
}

/**
 * Display line for timeline nodes: `YYYY / MM / DD` (real-world scene date when set).
 * @param {Record<string, unknown>} snapshot
 * @param {Record<string, unknown> | null} [capture] — for createdAt fallback
 */
export function formatSceneDateDisplay(snapshot, capture) {
  const ms =
    snapshotCreatedMs(snapshot?.sceneDate) ||
    snapshotCreatedMs(capture?.createdAt) ||
    snapshotCreatedMs(snapshot?.createdAt);
  if (!ms) return '— / — / —';
  try {
    const d = new Date(ms);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y} / ${m} / ${day}`;
  } catch {
    return '— / — / —';
  }
}

/** Ordinal day suffix (1ST, 2ND, 3RD, 4TH, …). */
function ordinalSuffixDay(day) {
  const j = day % 10;
  const k = day % 100;
  if (j === 1 && k !== 11) return 'ST';
  if (j === 2 && k !== 12) return 'ND';
  if (j === 3 && k !== 13) return 'RD';
  return 'TH';
}

/**
 * Below-rail scene date line: `2ND MARCH 2026`, uppercase.
 * Uses the **local** calendar so it matches the timeline detail panel (`sceneDate` / datetime input),
 * which stores instants via `Timestamp.fromDate(new Date(sceneIso))` (local interpretation).
 * @param {number} ms — epoch ms from {@link snapshotTimelineStepMs} or 0 / invalid
 * @returns {string}
 */
export function formatSceneDateOrdinal(ms) {
  if (!ms || ms <= 0) return '—';
  try {
    const d = new Date(ms);
    const day = d.getDate();
    const month = d.toLocaleDateString('en-GB', { month: 'long' });
    const year = d.getFullYear();
    return `${day}${ordinalSuffixDay(day)} ${month.toUpperCase()} ${year}`;
  } catch {
    return '—';
  }
}

/** @deprecated Use {@link formatSceneDateOrdinal} — name referred to old UTC behaviour. */
export function formatSceneDateOrdinalUtc(ms) {
  return formatSceneDateOrdinal(ms);
}

/**
 * @param {Array<Record<string, unknown>>} snapshots
 * @returns {Array<Record<string, unknown>>}
 */
export function sortTimelineSnapshots(snapshots) {
  if (!Array.isArray(snapshots) || snapshots.length === 0) return [];
  return [...snapshots].sort((a, b) => {
    const msA = snapshotTimelineSortMs(a);
    const msB = snapshotTimelineSortMs(b);
    if (msA !== msB) return msA - msB;
    const oa = typeof a.orderIndex === 'number' ? a.orderIndex : null;
    const ob = typeof b.orderIndex === 'number' ? b.orderIndex : null;
    if (oa != null && ob != null && oa !== ob) return oa - ob;
    if (oa != null && ob == null) return -1;
    if (oa == null && ob != null) return 1;
    return String(a.id || '').localeCompare(String(b.id || ''));
  });
}

/**
 * Timeline steps shown in UI: `included !== false` (default included).
 * @param {Array<Record<string, unknown>>} snapshots
 */
export function filterIncludedTimelineSnapshots(snapshots) {
  return sortTimelineSnapshots(snapshots).filter((s) => s.included !== false);
}

/**
 * @param {object} snapshot
 * @param {string} mode — 'version' | 'date' | 'custom'
 * @param {number} versionIndex — 0-based among included steps
 * @param {import('firebase/firestore').Timestamp | undefined} captureCreatedAt — optional capture `createdAt` for date mode
 */
export function formatTimelineStepLabel(snapshot, mode, versionIndex, captureCreatedAt) {
  const custom = typeof snapshot.label === 'string' ? snapshot.label.trim() : '';
  if (mode === 'custom' && custom) return custom;
  if (mode === 'version' || mode === 'custom') {
    return `V${versionIndex + 1}`;
  }
  if (mode === 'date') {
    const ts = snapshot.sceneDate || captureCreatedAt || snapshot.createdAt;
    const ms = snapshotCreatedMs(ts);
    if (ms > 0) {
      try {
        return new Date(ms).toISOString().slice(0, 10);
      } catch {
        return custom || `V${versionIndex + 1}`;
      }
    }
  }
  return custom || snapshot.captureId || snapshot.id || '';
}

/**
 * Coordinates for timeline sub-label: latitude row, then longitude (each value, then N/S or E/W).
 * @param {number} lat
 * @param {number} lng
 */
function formatCoordinatesSubLabel(lat, lng) {
  const latRef = lat >= 0 ? 'N' : 'S';
  const lngRef = lng >= 0 ? 'E' : 'W';
  return `${Math.abs(lat).toFixed(5)} ${latRef}\n${Math.abs(lng).toFixed(5)} ${lngRef}`;
}

/**
 * Second line under each capture title on the architecture timeline / capture switcher.
 * Firestore `timelineSubLabelMode`: version | days | coordinates | custom | none.
 * @param {string} timelineSubLabelMode
 * @param {number} ordinalDayNum — 1-based index in the included timeline
 * @param {Record<string, unknown> | null | undefined} capture
 * @param {Record<string, unknown> | null | undefined} [snapshot] — for `custom`: uses `snapshot.label`
 * @returns {string | null}
 */
export function formatTimelineBelowRailSubLabel(timelineSubLabelMode, ordinalDayNum, capture, snapshot) {
  const mode = timelineSubLabelMode || 'days';
  if (mode === 'none') return null;
  if (mode === 'custom') {
    const raw = snapshot && typeof snapshot.label === 'string' ? snapshot.label.trim() : '';
    return raw ? formatTimelineCaptureLabel(raw) : null;
  }
  if (mode === 'days') return `DAY ${ordinalDayNum}`;
  if (mode === 'version') return `V${ordinalDayNum}`;
  if (mode === 'coordinates') {
    const pos = getCaptureLatLng(capture);
    if (!pos) return '—';
    return formatCoordinatesSubLabel(pos.lat, pos.lng);
  }
  return `DAY ${ordinalDayNum}`;
}
